import { useState, useEffect, useCallback, useRef, MutableRefObject } from 'react';
import { ScimEntityService } from '../../../services/ScimEntityService';
import CentralizedSchemaService from '../../../services/CentralizedSchemaService';
import { useResponsibilityCenterCache, useResponsibilityCenterActions } from '../../../store/responsibilityCenterHooks';
import {
    parseLegacyFilter,
    processFilterPipeline,
    batchResolveLegacyRcIds,
    type ParsedLegacyFilter,
    type RCResolutionDependencies
} from '../legacyFilterUtils';
import { debugLog, debugVerboseLog } from '../../../utils/debugLog';

/**
 * Custom hook for managing legacy filter conversion logic
 * Handles RC cache, untranslated IDs, filter parsing, and attribute validation
 */
export function useLegacyConversion(entityServiceRef: MutableRefObject<ScimEntityService | undefined>) {
    // ========== State Management ==========
    
    // Untranslated RC IDs (4-digit values that couldn't be resolved)
    const [untranslatedRcIds, setUntranslatedRcIds] = useState<Set<string>>(new Set());
    const [showClearRcIdsDialog, setShowClearRcIdsDialog] = useState(false);
    
    // Untranslated JobCodeMemberOf GUIDs
    const [untranslatedJobCodeGuids, setUntranslatedJobCodeGuids] = useState<Set<string>>(new Set());
    const [showClearJobCodeGuidsDialog, setShowClearJobCodeGuidsDialog] = useState(false);
    
    // Parse error state
    const [legacyParseError, setLegacyParseError] = useState<string | null>(null);
    
    // Attribute validation warnings
    const [attributeValidationWarning, setAttributeValidationWarning] = useState<string[]>([]);
    
    // ========== Redux Store Integration ==========
    
    const { cache: responsibilityCenterCache, isLoaded: rcCacheLoaded, loading: loadingRCCache } = useResponsibilityCenterCache();
    const { setCache: setResponsibilityCenterCache, addRC, setLoadingState: setLoadingRCCache } = useResponsibilityCenterActions();
    
    // ========== Ref-based Caches ==========
    
    // Cache for in-flight RC ID resolution requests to prevent duplicate API calls
    const rcIdResolutionPromisesRef = useRef<Map<string, Promise<string | null>>>(new Map());
    
    // Local cache that's updated immediately (before Redux state updates) to prevent duplicate API calls
    const localRCCacheRef = useRef<Map<string, string[]>>(new Map());
    
    // Cache for legacy IDs that were searched but not found (to prevent retrying them)
    const rcNotFoundCacheRef = useRef<Set<string>>(new Set());
    
    // Schema service
    const schemaService = CentralizedSchemaService.getInstance();
    
    // ========== LocalStorage Integration ==========
    
    // Load untranslated RC IDs from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem('untranslatedRcIds');
            if (stored) {
                const parsed = JSON.parse(stored) as string[];
                setUntranslatedRcIds(new Set(parsed));
            }
        } catch (err) {
            console.warn('Failed to load untranslated RC IDs from localStorage:', err);
        }
    }, []);
    
    // Load untranslated JobCode GUIDs from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem('untranslatedJobCodeGuids');
            if (stored) {
                const parsed = JSON.parse(stored) as string[];
                setUntranslatedJobCodeGuids(new Set(parsed));
            }
        } catch (err) {
            console.warn('Failed to load untranslated JobCode GUIDs from localStorage:', err);
        }
    }, []);
    
    // ========== Untranslated IDs Management ==========
    
    const addUntranslatedRcIds = useCallback((newIds: string[]) => {
        if (newIds.length === 0) return;
        
        setUntranslatedRcIds(prev => {
            const updated = new Set(prev);
            let hasNew = false;
            for (const id of newIds) {
                if (!updated.has(id)) {
                    updated.add(id);
                    hasNew = true;
                }
            }
            
            if (hasNew) {
                try {
                    localStorage.setItem('untranslatedRcIds', JSON.stringify(Array.from(updated).sort()));
                } catch (err) {
                    console.warn('Failed to save untranslated RC IDs to localStorage:', err);
                }
            }
            
            return updated;
        });
    }, []);
    
    const addUntranslatedJobCodeGuids = useCallback((newGuids: string[]) => {
        if (newGuids.length === 0) return;
        
        setUntranslatedJobCodeGuids(prev => {
            const updated = new Set(prev);
            let hasNew = false;
            for (const guid of newGuids) {
                if (!updated.has(guid)) {
                    updated.add(guid);
                    hasNew = true;
                }
            }
            
            if (hasNew) {
                try {
                    localStorage.setItem('untranslatedJobCodeGuids', JSON.stringify(Array.from(updated).sort()));
                } catch (err) {
                    console.warn('Failed to save untranslated JobCode GUIDs to localStorage:', err);
                }
            }
            
            return updated;
        });
    }, []);
    
    const clearUntranslatedRcIds = useCallback(() => {
        setUntranslatedRcIds(new Set());
        try {
            localStorage.removeItem('untranslatedRcIds');
        } catch (err) {
            console.warn('Failed to clear untranslated RC IDs from localStorage:', err);
        }
        setShowClearRcIdsDialog(false);
    }, []);
    
    const clearUntranslatedJobCodeGuids = useCallback(() => {
        setUntranslatedJobCodeGuids(new Set());
        try {
            localStorage.removeItem('untranslatedJobCodeGuids');
        } catch (err) {
            console.warn('Failed to clear untranslated JobCode GUIDs from localStorage:', err);
        }
        setShowClearJobCodeGuidsDialog(false);
    }, []);
    
    // ========== RC Cache Management ==========
    
    const loadResponsibilityCenterCache = useCallback(async () => {
        if (rcCacheLoaded) {
            return;
        }
        
        if (loadingRCCache) {
            debugLog('🔍 useLegacyConversion: loadResponsibilityCenterCache already in progress, waiting...');
            let waitCount = 0;
            while (loadingRCCache && !rcCacheLoaded && waitCount < 50) {
                await new Promise(resolve => setTimeout(resolve, 100));
                waitCount++;
            }
            if (rcCacheLoaded) {
                debugLog('✅ useLegacyConversion: Cache loaded by another call');
            }
            return;
        }
        
        debugLog('🔍 useLegacyConversion: Starting loadResponsibilityCenterCache');
        setLoadingRCCache(true);
        try {
            const cache = new Map<string, string[]>();
            let page = 1;
            const pageSize = 500;
            let hasMore = true;
            let totalLoaded = 0;
            let totalPages = 0;
            
            while (hasMore) {
                totalPages++;
                debugVerboseLog(`🔍 useLegacyConversion: Loading RC cache page ${page} (pageSize: ${pageSize})`);
                const response = await entityServiceRef.current!.getEntitiesByType('ResponsibilityCenter', page, pageSize, undefined, undefined, ['Id', 'DisplayName', 'Ids']);
                
                const rcs = response.data || [];
                rcs.forEach((rc: any) => {
                    const rcId = rc.Id || rc.id || '';
                    const ids = rc.Ids || rc.ids || [];
                    if (rcId) {
                        cache.set(rcId, Array.isArray(ids) ? ids : []);
                        totalLoaded++;
                    }
                });
                
                hasMore = response.hasNextPage || false;
                if (hasMore) {
                    page++;
                }
            }
            
            debugLog(`🔍 useLegacyConversion: Completed loading RC cache in ${totalPages} page(s)`);
            
            setResponsibilityCenterCache(cache);
            localRCCacheRef.current = new Map(cache);
            
            let totalLegacyIds = 0;
            cache.forEach((ids) => {
                totalLegacyIds += ids.length;
            });
            debugLog(`✅ useLegacyConversion: Loaded ${totalLoaded} responsibility centers into cache with ${totalLegacyIds} total legacy IDs`);
            
            if (totalLoaded > 0 && totalLegacyIds === 0) {
                console.warn(`⚠️ useLegacyConversion: Loaded ${totalLoaded} RCs but found 0 legacy IDs. Cache structure might be incorrect.`);
                const sampleRc = Array.from(cache.entries())[0];
                if (sampleRc) {
                    console.warn(`  Sample RC: ID="${sampleRc[0]}", Ids=`, sampleRc[1]);
                }
            }
        } catch (err) {
            console.error('Error loading responsibility center cache:', err);
        } finally {
            setLoadingRCCache(false);
        }
    }, [rcCacheLoaded, loadingRCCache, setLoadingRCCache, setResponsibilityCenterCache, entityServiceRef]);
    
    // ========== Helper Functions ==========
    
    const getLegacyIdToRcIdMap = useCallback((): Map<string, string> => {
        const reverseMap = new Map<string, string>();
        let totalLegacyIds = 0;
        
        localRCCacheRef.current.forEach((legacyIds, rcId) => {
            if (Array.isArray(legacyIds)) {
                legacyIds.forEach(legacyId => {
                    if (legacyId) {
                        reverseMap.set(legacyId, rcId);
                        totalLegacyIds++;
                    }
                });
            }
        });
        
        responsibilityCenterCache.forEach((legacyIds, rcId) => {
            if (Array.isArray(legacyIds)) {
                legacyIds.forEach(legacyId => {
                    if (legacyId && !reverseMap.has(legacyId)) {
                        reverseMap.set(legacyId, rcId);
                        totalLegacyIds++;
                    }
                });
            }
        });
        
        const cacheSize = responsibilityCenterCache.size + localRCCacheRef.current.size;
        if (cacheSize > 0 && totalLegacyIds === 0) {
            console.warn(`⚠️ getLegacyIdToRcIdMap: Cache has ${cacheSize} RCs but no legacy IDs found. Cache structure might be incorrect.`);
        }
        
        return reverseMap;
    }, [responsibilityCenterCache]);
    
    const getRCResolutionDeps = useCallback((): RCResolutionDependencies => {
        return {
            entityService: entityServiceRef.current!,
            getLegacyIdToRcIdMap,
            addRC,
            localRCCacheRef,
            responsibilityCenterCache,
            rcIdResolutionPromisesRef,
            rcNotFoundCacheRef,
            rcCacheLoaded,
            loadingRCCache
        };
    }, [getLegacyIdToRcIdMap, addRC, responsibilityCenterCache, rcCacheLoaded, loadingRCCache, entityServiceRef]);
    
    const processFilterPipelineWithDeps = useCallback(async (filterString: string): Promise<string> => {
        return processFilterPipeline(filterString, getRCResolutionDeps());
    }, [getRCResolutionDeps]);
    
    const parseLegacyFilterWithErrorHandling = useCallback((legacyFilterText: string): ParsedLegacyFilter | null => {
        const result = parseLegacyFilter(legacyFilterText);
        if (result?.error) {
            setLegacyParseError(result.error);
            return null;
        }
        setLegacyParseError(null);
        
        if (result?.untranslatedJobCodeGuids && result.untranslatedJobCodeGuids.length > 0) {
            addUntranslatedJobCodeGuids(result.untranslatedJobCodeGuids);
        }
        
        return result;
    }, [addUntranslatedJobCodeGuids]);
    
    const cleanSetDisplayName = useCallback((name: string): string => {
        if (!name) return name;
        let cleaned = name.replace(/[\n\r\t]/g, ' ');
        cleaned = cleaned.replace(/\s+/g, ' ');
        return cleaned.trim();
    }, []);
    
    const extractAttributesFromFilter = useCallback((filter: string): string[] => {
        const attributePattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s+(?:eq|ne|co|sw|ew|gt|ge|lt|le|pr|gt|ge)\b/gi;
        const matches = filter.matchAll(attributePattern);
        const attributes = new Set<string>();
        
        for (const match of matches) {
            if (match[1]) {
                attributes.add(match[1]);
            }
        }
        
        return Array.from(attributes);
    }, []);
    
    const validateAttributesForPerson = useCallback(async (filter: string) => {
        try {
            const attributes = extractAttributesFromFilter(filter);
            if (attributes.length === 0) {
                setAttributeValidationWarning([]);
                return;
            }
            
            const schemaData = await schemaService.getSchemaData();
            const personSchema = schemaData.schemas.find((schema: any) =>
                schema.name?.toLowerCase() === 'person'
            );
            
            if (!personSchema) {
                setAttributeValidationWarning([]);
                return;
            }
            
            const personAttributeNames = new Set(
                (personSchema.attributes || []).map((attr: any) => attr.name?.toLowerCase())
            );
            
            const missingAttributes = attributes.filter(attr =>
                !personAttributeNames.has(attr.toLowerCase())
            );
            
            setAttributeValidationWarning(missingAttributes);
        } catch (err) {
            console.error('Error validating attributes:', err);
            setAttributeValidationWarning([]);
        }
    }, [extractAttributesFromFilter, schemaService]);
    
    // ========== Return Hook API ==========
    
    return {
        // State
        untranslatedRcIds,
        showClearRcIdsDialog,
        setShowClearRcIdsDialog,
        untranslatedJobCodeGuids,
        showClearJobCodeGuidsDialog,
        setShowClearJobCodeGuidsDialog,
        legacyParseError,
        setLegacyParseError,
        attributeValidationWarning,
        setAttributeValidationWarning,
        
        // RC Cache
        responsibilityCenterCache,
        rcCacheLoaded,
        loadingRCCache,
        localRCCacheRef,
        rcNotFoundCacheRef,
        loadResponsibilityCenterCache,
        
        // Functions
        addUntranslatedRcIds,
        addUntranslatedJobCodeGuids,
        clearUntranslatedRcIds,
        clearUntranslatedJobCodeGuids,
        getLegacyIdToRcIdMap,
        getRCResolutionDeps,
        processFilterPipelineWithDeps,
        parseLegacyFilterWithErrorHandling,
        cleanSetDisplayName,
        extractAttributesFromFilter,
        validateAttributesForPerson,
        batchResolveLegacyRcIds: (deps: RCResolutionDependencies, legacyIds: string[]) => batchResolveLegacyRcIds(legacyIds, deps)
    };
}

